import { useMemo, useState } from "react";
import Button from "@/components/common/Button";
import Reveal from "@/components/common/Reveal";
import CountUp from "@/components/common/CountUp";
import Pill from "@/components/common/Pill";
import GridBackdrop from "@/components/visual/GridBackdrop";
import SpinningCoin from "@/components/visual/SpinningCoin";
import { ArrowRight } from "@/components/common/icons";
import {
  ASSET_CATEGORY_CHIPS,
  CATEGORY_GROUP_MEMBERS,
  INSTRUMENT_TABLE,
  INSTRUMENT_COUNTS,
  type AssetCategoryFilter,
} from "@/lib/data";
import { usePageMeta } from "@/lib/usePageMeta";
import { useLocale } from "@/i18n/useLocale";
import { tradableAssetsFor } from "@/content/tradable-assets";
import type { TradableAssetsContent } from "@/content/types";
import { useBreadcrumbStructuredData } from "@/lib/seo";
import { env } from "@/lib/env";
import { cx } from "@/lib/cx";

function Hero({ t }: { t: TradableAssetsContent }): JSX.Element {
  return (
    <section className="relative pt-[calc(var(--header-h,72px)+72px)] pb-2 md:pb-12 overflow-hidden">
      <div className="brand-glow" style={{ height: 360 }} aria-hidden="true" />
      <div className="container-page relative z-[2]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16">
          <div className="flex-1 max-w-[620px]">
            <h1
              className="font-display font-bold"
              style={{ fontSize: "clamp(40px, 4.5vw, 72px)", lineHeight: 0.96, letterSpacing: "-0.04em" }}
            >
              {t.hero.line1}
              <br />
              {t.hero.line2}
              <br className="md:hidden" />{" "}
              <span className="text-brand-gradient">{t.hero.highlight}</span>
            </h1>
            <p className="text-fg-muted text-base md:text-[18px] max-w-[560px] mt-5">
              {t.hero.lede}
            </p>
          </div>
          <div
            className="hidden lg:flex lg:flex-shrink-0 lg:items-center lg:justify-center"
            aria-hidden="true"
          >
            <SpinningCoin size="clamp(180px, 18vw, 280px)" />
          </div>
        </div>
      </div>
    </section>
  );
}

function CountsStrip({ t }: { t: TradableAssetsContent }): JSX.Element {
  return (
    <div className="glass-card grid gap-px overflow-hidden grid-cols-2 md:grid-cols-4 mt-5" style={{ background: "rgb(var(--border))" }}>
      {INSTRUMENT_COUNTS.map(([countKey, count]) => {
        // INSTRUMENT_COUNTS values are mostly clean ints with the
        // open-ended "200+" cell. parseInt strips the "+" cleanly;
        // the format function reattaches the suffix on every tick
        // so the count animates 0+ → 200+.
        const hasPlus = count.endsWith("+");
        const target = parseInt(count, 10);
        const suffix = hasPlus ? "+" : "";
        return (
          <div key={countKey} className="bg-card px-3 py-3 md:px-5 md:py-5">
            <div
              className="font-display tracking-tight leading-none"
              style={{ fontSize: "clamp(20px, 2.2vw, 28px)", letterSpacing: "-0.02em" }}
            >
              <CountUp to={target} duration={1400} format={(n) => `${Math.round(n)}${suffix}`} />
            </div>
            <div className="text-[10px] md:text-[11px] text-fg-muted mt-1 tracking-wide">{t.counts[countKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

interface InstrumentRowsProps {
  category: AssetCategoryFilter;
}

function InstrumentRows({ category, t }: InstrumentRowsProps & { t: TradableAssetsContent }): JSX.Element {
  const rows = useMemo(() => {
    const allowed = CATEGORY_GROUP_MEMBERS[category];
    return INSTRUMENT_TABLE.filter((r) => allowed.includes(r.category));
  }, [category]);

  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={3} className="text-center text-fg-muted py-12">
          {t.emptyState}
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.map((r) => (
        <tr key={r.symbol}>
          <td className="font-bold w-1/3">{r.symbol}</td>
          <td className="w-1/3 text-center py-2">
            <div className="inline-flex items-center justify-center">
              <r.Logo />
            </div>
          </td>
          <td className="w-1/3 text-right">
            <Pill variant="muted" className="font-mono text-[9.5px] tracking-[0.12em]">
              {t.categories[r.category]}
            </Pill>
          </td>
        </tr>
      ))}
    </>
  );
}

export default function TradableAssetsPage(): JSX.Element {
  const t = tradableAssetsFor(useLocale());
  usePageMeta({
    title: t.meta.title,
    description: t.meta.description,
  });
  useBreadcrumbStructuredData([
    { name: "Home", url: "/" },
    { name: "Tradable Assets", url: "/tradable-assets" },
  ]);

  const [category, setCategory] = useState<AssetCategoryFilter>("ALL");

  return (
    <>
      <GridBackdrop />
      <div className="animate-page-in relative z-[1]">
        <Hero t={t} />
        <section className="pt-2 pb-16 md:py-16 2xl:py-24">
          <div className="container-page">
          {/* Category chips */}
          <div className="flex flex-wrap gap-2 items-center">
            {ASSET_CATEGORY_CHIPS.map((c) => {
              const active = category === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={cx(
                    "font-mono text-[12px] px-4 py-2 rounded-full font-semibold tracking-[0.08em] uppercase border transition-all",
                    active ? "bg-brand border-brand text-white" : "bg-bg-2 border-border text-fg hover:border-border-strong",
                  )}
                  style={
                    active
                      ? { boxShadow: "0 8px 20px -6px rgb(var(--brand) / 0.5)" }
                      : undefined
                  }
                  aria-pressed={active}
                >
                  {t.chips[c]}
                </button>
              );
            })}
          </div>

          <CountsStrip t={t} />

          <Reveal>
            <div className="glass-card mt-5 overflow-hidden">
              <table className="assets-table">
                <thead>
                  <tr>
                    <th className="w-1/3">{t.table.symbol}</th>
                    <th className="w-1/3 text-center" aria-hidden="true" />
                    <th className="w-1/3 text-right">{t.table.category}</th>
                  </tr>
                </thead>
                <tbody>
                  <InstrumentRows category={category} t={t} />
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Full-bleed CTA strip — its own block */}
      <section className="section-y-lg">
        <div className="container-page">
          <Reveal>
            <div
              className="glass-card glass-card-glow p-8 md:p-12 flex justify-between items-center gap-6 md:gap-8 flex-wrap"
              style={{
                background:
                  "linear-gradient(135deg, rgb(var(--brand) / 0.18), transparent 60%), linear-gradient(180deg, rgb(var(--fg) / 0.04) 0%, rgb(var(--fg) / 0.01) 100%)",
              }}
            >
              <div>
                <h3 className="font-display" style={{ fontSize: "clamp(22px, 2.6vw, 32px)", lineHeight: 1.05 }}>
                  Trade <span className="text-brand-gradient">300+ instruments</span> on one account.
                </h3>
                <p className="text-fg-muted mt-2 text-sm">
                  {t.footnote}
                </p>
              </div>
              <Button size="lg" variant="primary" href={env.registerUrl}>
                Start trading <ArrowRight />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
      </div>
    </>
  );
}
